/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// import { setIsValid } from '../user';

export type Status = 'initial' | 'success' | 'error';
export type Source =
  | 'contactForm'
  | 'whatsapp'
  | 'showPhone'
  | 'multipleLeadsModal';

export type LeadAnalyticsData = {
  category?: string | null;
  city?: string | null;
  currency?: string | null;
  operation?: string | null;
  province?: string | null;
  region?: string | null;
  value?: number | string | null;
};

export type ContactFormState = {
  isContactButtonLoading: boolean;
  isMobileModalOpen: boolean;
  isWhatsappButtonLoading: boolean;
  isShowPhoneButtonLoading: boolean;
  isLeadLimitReached: boolean;
  message: string;
  isContactFormSent: boolean;
  isWhatsappFormSent: boolean;
  isShowPhoneFormSent: boolean;
  source: Source;
  status: Status;
  whatsappOpened: boolean;
  isValidMessage: boolean;
};

export type SendLegacyTerrainContactPayload = LeadAnalyticsData & {
  code?: null | string;
  legacyTerrainId: number;
  legacyTerrainTitle: string;
  legacyTerrainUri: string;
  recaptchaToken: string;
  recaptchaAction: string;
};

export type SendLegacyTerrainWhatsappContactPayload =
  SendLegacyTerrainContactPayload;

export type SendLegacyProjectContactPayload = LeadAnalyticsData & {
  legacyProjectId: number;
  legacyProjectCommuneId: number;
  recaptchaToken: string;
  recaptchaAction: string;
};

export type SetMessagePayload = {
  message: string;
  isValid: boolean;
};

export type SetMobileModalPayload = {
  isMobileModalOpen: boolean;
  source: Source;
};

export type SendLegacyProjectWhatsappContactPayload =
  SendLegacyProjectContactPayload;

export type SendLegacyProjectShowPhoneContactPayload =
  SendLegacyProjectContactPayload;

export const initialState: ContactFormState = {
  isLeadLimitReached: false,
  isContactButtonLoading: false,
  isMobileModalOpen: false,
  isShowPhoneButtonLoading: false,
  isValidMessage: true,
  isWhatsappButtonLoading: false,
  message: '',
  isContactFormSent: false,
  isWhatsappFormSent: false,
  isShowPhoneFormSent: false,
  source: 'contactForm',
  status: 'initial',
  whatsappOpened: false
};

const contactForm = createSlice({
  name: 'contactForm',
  initialState,
  reducers: {
    reset: (state) => {
      return {
        ...initialState,
        message: state.message
      };
    },
    setMessage: (state, action: PayloadAction<SetMessagePayload>) => {
      state.message = action.payload.message;
      state.isValidMessage = action.payload.isValid;
    },
    setStatus: (state, action: PayloadAction<Status>) => {
      state.status = action.payload;
      state.isLeadLimitReached = false;
      state.isWhatsappButtonLoading = false;
      state.isContactButtonLoading = false;
      state.isShowPhoneButtonLoading = false;

      if (state.source !== 'whatsapp') {
        state.isMobileModalOpen = false; // close the possible mobile modal
      }

      if (action.payload === 'error' || action.payload === 'initial') {
        if (state.source === 'whatsapp') {
          state.isWhatsappFormSent = false;
        } else if (state.source === 'contactForm') {
          state.isContactFormSent = false;
        } else if (state.source === 'showPhone') {
          state.isShowPhoneFormSent = false;
        }
      }
    },
    setIsMobileModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isMobileModalOpen = action.payload;
    },
    setMobileModal: (state, action: PayloadAction<SetMobileModalPayload>) => {
      state.isMobileModalOpen = action.payload.isMobileModalOpen;
      state.source = action.payload.source;
      state.status = 'initial';
    },
    setWhatsappOpened: (state, action: PayloadAction<boolean>) => {
      state.whatsappOpened = action.payload;
      state.isMobileModalOpen = false;
    },
    sendLegacyTerrainContact: (
      state,
      _: PayloadAction<SendLegacyTerrainContactPayload>
    ) => {
      state.isContactButtonLoading = true;
      state.source = 'contactForm';
      state.isContactFormSent = true;
    },
    sendLegacyTerrainShowPhoneContact: (
      state,
      _: PayloadAction<SendLegacyTerrainContactPayload>
    ) => {
      state.isShowPhoneButtonLoading = true;
      state.source = 'showPhone';
      state.isShowPhoneFormSent = true;
    },
    sendLegacyTerrainWhatsappContact: (
      state,
      _: PayloadAction<SendLegacyTerrainContactPayload>
    ) => {
      state.isWhatsappButtonLoading = true;
      state.source = 'whatsapp';
      state.isWhatsappFormSent = true;
    },
    sendLegacyProjectContact: (
      state,
      _: PayloadAction<SendLegacyProjectContactPayload>
    ) => {
      state.isContactButtonLoading = true;
      state.source = 'contactForm';
      state.isContactFormSent = true;
    },
    sendLegacyProjectShowPhoneContact: (
      state,
      _: PayloadAction<SendLegacyProjectShowPhoneContactPayload>
    ) => {
      state.isShowPhoneButtonLoading = true;
      state.source = 'showPhone';
      state.isShowPhoneFormSent = true;
    },
    sendLegacyProjectWhatsappContact: (
      state,
      _: PayloadAction<SendLegacyProjectWhatsappContactPayload>
    ) => {
      state.isWhatsappButtonLoading = true;
      state.source = 'whatsapp';
      state.isWhatsappFormSent = true;
    },
    setIsLeadLimitReached: (state, action: PayloadAction<boolean>) => {
      state.isLeadLimitReached = action.payload;
    }
  }
});

export const {
  reset,
  sendLegacyProjectContact,
  sendLegacyProjectShowPhoneContact,
  sendLegacyProjectWhatsappContact,
  sendLegacyTerrainContact,
  sendLegacyTerrainShowPhoneContact,
  sendLegacyTerrainWhatsappContact,
  setIsLeadLimitReached,
  setIsMobileModalOpen,
  setMessage,
  setMobileModal,
  setStatus,
  setWhatsappOpened
} = contactForm.actions;

export default contactForm.reducer;
